import React from 'react';
import { Users, Building2, Calendar, CreditCard } from 'lucide-react';
import { computeDelta, deltaColor, formatMoney } from './analyticsHelpers';
import { MiniSparkline } from './MiniSparkline';

const colStyle = {
  flex: '1 1 260px',
  minWidth: 0,
  background: '#FFFFFF',
  borderRadius: 16,
  padding: 14,
  border: '1px solid rgba(112, 63, 246, 0.08)',
  boxShadow: 'var(--premium-shadow-nested)',
};

const colHeaderStyle = {
  fontFamily: "'Inria Serif', serif",
  fontSize: 12,
  fontWeight: 700,
  color: '#999',
  letterSpacing: 1,
  textTransform: 'uppercase',
  margin: '0 0 8px',
};

const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 0',
  borderTop: '1px solid #F2F2F2',
};

const rowFirst = { ...rowStyle, borderTop: 'none' };

const PulseRow = ({ icon, label, value, delta, isMoney = false, sparkValues, sparkColor }) => (
  <div style={rowStyle} data-testid={`pulse-row-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <span style={{ color: '#999', display: 'flex', alignItems: 'center' }}>{icon}</span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={{ margin: 0, fontSize: 11, color: '#888' }}>{label}</p>
      <p style={{ margin: '2px 0 0', fontSize: 16, fontWeight: 700, color: '#111' }}>
        {isMoney ? formatMoney(value) : value}
      </p>
    </div>
    {Array.isArray(sparkValues) ? (
      <MiniSparkline values={sparkValues} color={sparkColor || '#703FF6'} width={70} height={22} ariaLabel={`${label} trend`} />
    ) : null}
    {delta && (
      <span style={{ fontSize: 11, fontWeight: 700, color: deltaColor(delta), whiteSpace: 'nowrap' }}>{delta.label}</span>
    )}
  </div>
);

const Column = ({ title, dataset, prevDataset, comparisonLabel, sparkData }) => {
  const guestsDelta = computeDelta(dataset?.guests, prevDataset?.guests);
  const hostsDelta = computeDelta(dataset?.hosts, prevDataset?.hosts);
  const venuesDelta = computeDelta(dataset?.venues, prevDataset?.venues);
  const bookingsDelta = computeDelta(dataset?.bookings, prevDataset?.bookings);
  const revenueDelta = computeDelta(dataset?.revenue, prevDataset?.revenue);
  return (
    <div style={colStyle}>
      <p style={colHeaderStyle}>{title}</p>
      <p style={{ margin: '0 0 8px', fontSize: 10, color: '#bbb' }}>vs {comparisonLabel}</p>
      <div>
        <div style={rowFirst}>
          <span style={{ color: '#999', display: 'flex', alignItems: 'center' }}><Users size={14} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 11, color: '#888' }}>New guests</p>
            <p style={{ margin: '2px 0 0', fontSize: 16, fontWeight: 700, color: '#111' }}>{dataset?.guests || 0}</p>
          </div>
          {Array.isArray(sparkData?.guests) ? (
            <MiniSparkline values={sparkData.guests} color="#00B341" width={70} height={22} ariaLabel="New guests trend" />
          ) : null}
          <span style={{ fontSize: 11, fontWeight: 700, color: deltaColor(guestsDelta), whiteSpace: 'nowrap' }}>{guestsDelta.label}</span>
        </div>
        <PulseRow icon={<Users size={14} />} label="New hosts" value={dataset?.hosts || 0} delta={hostsDelta} sparkValues={sparkData?.hosts} sparkColor="#F59E0B" />
        <PulseRow icon={<Building2 size={14} />} label="New venues" value={dataset?.venues || 0} delta={venuesDelta} sparkValues={sparkData?.venues} sparkColor="#703FF6" />
        <PulseRow icon={<Calendar size={14} />} label="Transactions" value={dataset?.bookings || 0} delta={bookingsDelta} sparkValues={sparkData?.bookings} sparkColor="#2F79EA" />
        <PulseRow icon={<CreditCard size={14} />} label="Allure revenue" value={dataset?.revenue || 0} delta={revenueDelta} isMoney sparkValues={sparkData?.revenue} sparkColor="#DD0608" />
      </div>
    </div>
  );
};

export const PulseStrip = ({ pulse, loading, sparkData }) => {
  if (loading && !pulse) {
    return (
      <div data-testid="pulse-strip-loading" style={{ padding: 20, textAlign: 'center', color: '#999' }}>
        Loading pulse…
      </div>
    );
  }
  if (!pulse) return null;
  return (
    <div data-testid="pulse-strip">
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Column title="Today" comparisonLabel="yesterday" dataset={pulse.today} prevDataset={pulse.yesterday} sparkData={sparkData} />
        <Column title="This Week" comparisonLabel="last week" dataset={pulse.week} prevDataset={pulse.prev_week} sparkData={sparkData} />
        <Column title="This Month" comparisonLabel="last month" dataset={pulse.month} prevDataset={pulse.prev_month} sparkData={sparkData} />
      </div>
    </div>
  );
};
